import { useState } from 'react'
import { Form, Formik, type FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'
import Axios, { type AxiosRequestConfig } from 'axios'
import dayjs from 'dayjs'
import { ParcelQuestionmark } from '@dhl/illustrations'
import { type GridColDef, Pagination } from '@dhl/components'
import { SelectInput, TextFieldInput } from '@dhl/formik'
import { CloseIcon } from '@dhl-ui/icons'
import { Body1, Box, Button, Grid, H6, IconButton, Link, MenuItem, Paper } from '@dhl-ui/components'
import { AdminDataGrid, useNotificationMessage } from '~/common'

import { EmailStatusDetails } from '~/modules/checkemails/components'
import type {
  EmailContactDetailsData,
  EmailLastMessagesData,
  EmailMessageStatisticsData,
  EmailStatus,
} from '~/modules/checkemails/model'
import { createEmailValidationSchema } from '~/modules/checkemails/helpers'

export type FormValues = {
  emailAddress: string
  businessUnit: string
  domain: string
}

type CheckEmailsResponseMessages = {
  count: number
  total: number
  messages: EmailLastMessagesData[]
}
type CheckEmailsResponse = [
  EmailContactDetailsData,
  CheckEmailsResponseMessages | EmailMessageStatisticsData,
  CheckEmailsResponseMessages | EmailMessageStatisticsData,
]

export const CheckEmailPage = () => {
  const { t } = useTranslation()
  const { showError } = useNotificationMessage()
  const validationSchema = createEmailValidationSchema(t)
  const [checkEmailStatus, setCheckEmailStatus] = useState<boolean>(false)
  const [emailContactDetailsData, setEmailContactDetailsData] = useState<EmailContactDetailsData>()
  const [emailMessageStatisticsData, setEmailMessageStatisticsData] = useState<EmailMessageStatisticsData>()
  const [lastMessagesData, setLastMessagesData] = useState<EmailLastMessagesData[]>()
  const [emailStatusNoData, setEmailStatusNoData] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const contactDetails: EmailStatus[] = [
    {
      title: `${t('admin.checkEmailsPage.emailAddress')}`,
      value: emailContactDetailsData?.email,
    },
    {
      title: `${t('admin.checkEmailsPage.lastActivityOn')}`,
      value: emailContactDetailsData?.lastActivityAt,
    },
    {
      title: `${t('admin.checkEmailsPage.firstEmailOn')}`,
      value: emailContactDetailsData?.createdAt,
    },
    {
      title: `${t('admin.checkEmailsPage.spamComplaining')}`,
      value: emailContactDetailsData?.isSpamComplaining,
    },
  ]

  const messageStatisticsDetails: EmailStatus[] = [
    {
      title: `${t('admin.checkEmailsPage.total')}`,
      value: emailMessageStatisticsData?.processedCount,
    },
    {
      title: `${t('admin.checkEmailsPage.delivered')}`,
      value: emailMessageStatisticsData?.deliveredCount,
    },
    {
      title: `${t('admin.checkEmailsPage.opened')}`,
      value: emailMessageStatisticsData?.openedCount,
    },
    {
      title: `${t('admin.checkEmailsPage.clicked')}`,
      value: emailMessageStatisticsData?.clickedCount,
    },
    {
      title: `${t('admin.checkEmailsPage.blocked')}`,
      value: emailMessageStatisticsData?.blockedCount,
    },
    {
      title: `${t('admin.checkEmailsPage.softBounced')}`,
      value: emailMessageStatisticsData?.softBouncedCount,
    },
    {
      title: `${t('admin.checkEmailsPage.hardBounced')}`,
      value: emailMessageStatisticsData?.hardBouncedCount,
    },
    {
      title: `${t('admin.checkEmailsPage.markedAsSpam')}`,
      value: emailMessageStatisticsData?.spamComplaintCount,
    },
  ]

  const initialValues: FormValues = {
    emailAddress: '',
    businessUnit: 'dhl-nl',
    domain: 'DHL_PARCEL',
  }

  const onSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    try {
      await getEmailStatus(values.emailAddress, values.businessUnit, values.domain)
    } catch {
      helpers.setSubmitting(false)
    }
  }

  const getEmailStatus = async (emailAddress: string, businessUnit: string, domain: string) => {
    setLoading(true)
    const config: AxiosRequestConfig = {
      url: `/api/users/${emailAddress}/mailstatus?bu=${businessUnit}&domain=${domain}`,
      responseType: 'json',
    }
    Axios.request<CheckEmailsResponse>(config)
      .then(({ data, status }) => {
        setLoading(false)
        if (status === 200) {
          if (data.length > 1) {
            // it's not guaranteed that the data is in the correct order
            const [contactDetailsData, firstData, secondData] = data
            const { messages, total } = (
              'messages' in firstData ? firstData : secondData
            ) as CheckEmailsResponseMessages
            const stats = ('messages' in firstData ? secondData : firstData) as EmailMessageStatisticsData
            if (messages) {
              setEmailContactDetailsData(contactDetailsData)
              setEmailMessageStatisticsData(stats)
              setCheckEmailStatus(true)
              setEmailStatusNoData(false)
              const messagesWithIndex = messages.map((item, index: number) => ({
                ...item,
                id: index + 1,
              }))
              setLastMessagesData(messagesWithIndex)
              setTotalCount(total)
            } else {
              showError(t('admin.checkEmailsPage.errorMessage'))
            }
          } else {
            setEmailStatusNoData(true)
            setCheckEmailStatus(false)
          }
        } else {
          showError(t('admin.checkEmailsPage.errorMessage'))
        }
      })
      .catch(function (error) {
        setLoading(false)
        showError(JSON.stringify(error.message))
      })
  }

  const resetPage = () => {
    setCheckEmailStatus(false)
    setEmailStatusNoData(false)
  }

  const formatTableCellDateTime = (value: string | undefined) => {
    return value ? dayjs(value).format('DD-MM-YYYY HH:mm:ss') : ''
  }

  const renderCellEmailStatus = (value: string | undefined) => {
    return value ? value : ''
  }

  const renderCellSubject = (value: string | undefined) => {
    return value ? value : ''
  }

  const renderCellFailureState = (value: string | undefined) => {
    return value ? value : ''
  }

  const columns: GridColDef<EmailLastMessagesData>[] = [
    {
      field: 'arrivedAt',
      headerName: t('admin.checkEmailsPage.date'),
      width: 160,
      sortable: false,
      valueGetter: formatTableCellDateTime,
    },
    {
      field: 'status',
      headerName: t('admin.checkEmailsPage.emailStatus'),
      width: 70,
      sortable: false,
      valueGetter: renderCellEmailStatus,
    },
    {
      field: 'messageSubject',
      headerName: t('admin.checkEmailsPage.subject'),
      flex: 1,
      sortable: false,
      valueGetter: renderCellSubject,
    },
    {
      field: 'statePermanent',
      headerName: t('admin.checkEmailsPage.failureState'),
      width: 120,
      sortable: false,
      valueGetter: renderCellFailureState,
    },
  ]

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validateOnMount validationSchema={validationSchema}>
        {({ isSubmitting, setFieldValue }) => (
          <>
            <Form data-testid="checkEmailStatus-form">
              <Box pt={2} pb={2}>
                <Grid container spacing={2}>
                  <Grid>
                    <SelectInput
                      disabled={isSubmitting}
                      name="businessUnit"
                      label={t('admin.checkEmailsPage.businessUnitPlaceholder')}
                      data-testid="select-businessUnit"
                      fullWidth
                    >
                      <MenuItem value="dhl-nl">DHL_NL</MenuItem>
                      <MenuItem value="dhl-be">DHL_BE</MenuItem>
                      <MenuItem value="ccc">CCC</MenuItem>
                    </SelectInput>
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <TextFieldInput
                      fullWidth
                      label={t('admin.checkEmailsPage.emailPlaceholder')}
                      name="emailAddress"
                      type="text"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <IconButton aria-label="reset" type="reset" onClick={() => resetPage()}>
                              <CloseIcon />
                            </IconButton>
                          ),
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <SelectInput
                      name="domain"
                      label={t('admin.checkEmailsPage.domainPlaceholder')}
                      data-testid="select-domain"
                      fullWidth
                    >
                      <MenuItem value="DHL_PARCEL">DHL_PARCEL</MenuItem>
                      <MenuItem value="DHL_ECOMMERCE">DHL_ECOMMERCE</MenuItem>
                    </SelectInput>
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <Button
                      data-testid="submit-emailStatusRequest"
                      disabled={isSubmitting}
                      color="primary"
                      type="submit"
                      variant="contained"
                    >
                      {t('admin.checkEmailsPage.emailStatusButton')}
                    </Button>
                  </Grid>
                </Grid>
                {emailStatusNoData && (
                  <Grid>
                    <Box
                      pt={1}
                      pb={2}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        backgroundColor: 'common.white',
                        marginTop: 3,
                      }}
                    >
                      <ParcelQuestionmark
                        sx={{
                          width: 255,
                          height: 169,
                        }}
                      />
                    </Box>
                    <Grid>
                      <Box
                        pt={1}
                        pb={10}
                        sx={{
                          backgroundColor: 'common.white',
                        }}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <H6>{t('admin.checkEmailsPage.emailStatusNoData')}</H6>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {!checkEmailStatus && (
                  <Grid>
                    <Box pt={1} pb={10} display="flex" justifyContent="left">
                      <Body1 variant="tertiary">
                        {t('admin.checkEmailsPage.infoText')}
                        <Link
                          title="Mailjet statussen"
                          href="https://documentation.mailjet.com/hc/en-us/articles/360048398994-Email-statuses-all-metrics-explained-"
                          rel="noreferrer"
                          target="_blank"
                          external={true}
                        >
                          {t('admin.checkEmailsPage.mailjetDocument')}
                        </Link>
                        {t('admin.checkEmailsPage.infoTextOr')}
                        <br />
                        <Link
                          title="Mailjet Website"
                          href="https://documentation.mailjet.com/hc/en-us"
                          rel="noreferrer"
                          target="_blank"
                          external={true}
                        >
                          {t('admin.checkEmailsPage.mailjetWebsite')}
                        </Link>
                        {t('admin.checkEmailsPage.infoTextDetails1')}
                        <br />
                        {t('admin.checkEmailsPage.infoTextDetails2')}
                        <br />
                        {t('admin.checkEmailsPage.infoTextDetails3')}
                      </Body1>
                    </Box>
                  </Grid>
                )}
              </Box>
            </Form>
            {checkEmailStatus && (
              <Grid container direction="column">
                <Grid container>
                  <EmailStatusDetails contactDetailsData={contactDetails} messageStatsData={messageStatisticsDetails} />
                </Grid>
                <H6>
                  {t('admin.checkEmailsPage.lastMessages')}({totalCount})
                </H6>
                <Paper>
                  <AdminDataGrid
                    sx={{
                      '& .MuiDataGrid-cell': {
                        cursor: 'default',
                      },
                      '& .MuiDataGrid-row': {
                        cursor: 'default',
                      },
                    }}
                    loading={loading}
                    data-testid="emailStatus-table"
                    autoHeight
                    rows={lastMessagesData ?? []}
                    columns={columns}
                    paginationModel={{
                      page,
                      pageSize,
                    }}
                    rowHeight={48}
                    columnHeaderHeight={48}
                    disableRowSelectionOnClick
                    disableColumnFilter
                    hideFooter
                  />
                </Paper>
                <Box mt={2}>
                  <Pagination
                    page={page}
                    count={lastMessagesData?.length ?? 0}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={setPageSize}
                  />
                </Box>
              </Grid>
            )}
          </>
        )}
      </Formik>
    </>
  )
}
